export const Tags = Object.freeze({
  ContinueLearning: "ContinueLearning",
  TopPicks: "TopPicks",
  PlaylistsForUser: "PlaylistsForUser",
  InspiredbySubjects: "InspiredbySubjects",
});

export const Achievements = Object.freeze({
  Completion: "completion",
  Hours: "hours",
  Badges: "badges",
});

class BaseParser {
  getAttribute(e, attribute) {
    return e.getAttribute(attribute);
  }

  getTextContentByDataTag(e, tag) {
    return e.querySelector(`[data-tag="${tag}"]`).textContent;
  }
}

export class SlideParser extends BaseParser {
  tag = null;
  dom = document;

  constructor(tag, dom) {
    super();
    this.tag = tag;
    this.dom = dom;
  }

  parse() {
    const container = this.dom.querySelector(`div[data-tag="${this.tag}`);

    if (!container) {
      return {
        tag: this.tag,
        slides: [],
      };
    }

    const slidesEls = [...container.querySelectorAll("div.slick-slide")];

    const slides = slidesEls.map((o) => {
      const image = this.getImage(o);
      const body = this.getBody(o);
      const footer = this.getFooter(o);
      return {
        image,
        body,
        footer,
      };
    });

    return {
      tag: this.tag,
      slides,
    };
  }

  getBody(e) {
    const bodyEl = e.querySelector('div[data-tag="p-course-description"]');

    switch (this.tag) {
      case Tags.ContinueLearning:
      case Tags.TopPicks:
      case Tags.InspiredbySubjects:
        return this.getGenericBody(bodyEl);
      case Tags.PlaylistsForUser:
        return {
          ...this.getGenericBody(bodyEl),
          jumbotron: { ...this.getImageCurated(e) },
        };
      default:
        throw new Error(`${this.tag} does not match any of the allowed tags.`);
    }
  }

  getFooter(e) {
    const footerEl = e.querySelector(".p-actiontile-footer");

    switch (this.tag) {
      case Tags.ContinueLearning:
      case Tags.PlaylistsForUser:
        return {};
      case Tags.TopPicks:
      case Tags.InspiredbySubjects:
        return {
          stars: this.getStars(footerEl),
        };
      default:
        throw new Error(`${this.tag} does not match any of the allowed tags.`);
    }
  }

  getGenericBody(e) {
    const title = this.getTitle(e);
    const titleLink = e
      .querySelector('a[data-tag="p-title-link"]')
      .getAttribute("href");
    const [category, text] = this.getText(e);
    return {
      category,
      title,
      titleLink,
      text,
    };
  }

  getImageCurated(e) {
    const [imgEl, textEl] = e.querySelectorAll(
      ".p-actiontile-curator .p-link-def"
    );
    const text = this.getTitle(textEl);
    const img = imgEl.querySelector("img").getAttribute("src");

    return {
      text,
      textLink: this.getAttribute(textEl, "href"),
      img,
      imgLink: this.getAttribute(imgEl, "href"),
    };
  }

  getTitle(e) {
    return e.querySelector(".p-MultiLineFade").textContent;
  }

  getText(e) {
    return [...e.querySelectorAll(".p-text")].map((o) => o.textContent);
  }

  getImage(e) {
    const a = e.querySelector("a[data-tag='p-image-link']");
    const div = a.querySelector("div");

    const title = this.getAttribute(a, "title");
    const link = this.getAttribute(a, "href");
    const style = this.getAttribute(div, "style");
    const [, imgUrl] = style.match(/url\("([A-Za-z/_0-9.-]+)"\);/);

    return {
      title,
      link,
      imgUrl,
    };
  }

  getStars(e) {
    const count = [...e.querySelectorAll(".p-icon-star")].length;
    const halfStar = e.querySelector(".p-icon-star-half");
    return {
      count,
      hasHalfStar: !!halfStar,
    };
  }
}

export class UserSummaryParser extends BaseParser {
  dom = document;

  constructor(dom) {
    super();
    this.dom = dom;
  }

  parse() {
    return {
      user: this.getUser(),
      subjects: this.getSubjects(),
      playlists: this.getPlaylists(),
      achievements: this.getAchievements(),
    };
  }

  getUser() {
    const container = this.dom.querySelector(
      'div[data-tag="usercard-statsummary"]'
    );

    if (!container) {
      return {};
    }

    const a = container.querySelector('a[data-tag="User-Card-Avatar"]');
    const img = a.querySelector("img");

    const link = this.getAttribute(a, "href");
    const imgSrc = this.getAttribute(img, "src");

    return {
      link,
      imgSrc,
    };
  }

  getAchievements() {
    const container = this.dom.querySelector(
      'div[data-tag="usercard-statsummary"]'
    );

    if (!container) {
      return [];
    }

    const textLs = [...container.querySelectorAll("span.p-text")].map(
      (o) => o.textContent
    );

    const achievements = [
      ["Lifetime Completions", Achievements.Completion],
      ["Calculated based on cumulative training to date.", Achievements.Hours],
      ["Badges", Achievements.Badges],
    ].map(([l, type], i) => {
      const linkEl = container.querySelector(`a[title="${l}"]`);
      return {
        value: textLs[i],
        text: linkEl.textContent,
        link: linkEl.getAttribute("href"),
        type,
      };
    });

    return achievements;
  }

  getSubjects() {
    const container = this.dom.querySelector(
      'div[data-tag="SelectedSubjectsCard-SelectedSubjectsList"] > span > span > span'
    );

    if (!container) {
      return [];
    }

    return container.textContent.split(",");
  }

  getPlaylists() {
    const columns = [
      ...this.dom.querySelectorAll(".p-selectedplaylistscard .p-gridcol"),
    ];

    if (!columns) {
      return {};
    }

    return columns.map((c) => {
      const [i1, i2] = [...c.querySelectorAll(".p-panel")];

      return {
        value: i1.querySelector(".p-text").textContent,
        link: i1.querySelector(".p-link").getAttribute("href"),
        text: i2.querySelector(".p-text").textContent,
      };
    });
  }
}

export class MiscParser extends BaseParser {
  dom = document;

  constructor(dom) {
    super();
    this.dom = dom;
  }

  parse() {
    return {
      transcript: this.getTranscript(),
      pastDue: this.getPastDue(),
      assigned: this.getAssigned(),
    };
  }

  getTranscript() {
    const container = this.dom.querySelector(
      'div[data-tag="Transcript-Summary-Container"]'
    );

    if (!container) {
      return {
        view: null,
        data: [],
      };
    }

    const viewLink = container.querySelector(
      'a[data-tag="Transcript-Summary-View-Link"]'
    );
    const transcripts = [
      ["Transcript-Summary-Past-Due-Count", "Past due"],
      ["Transcript-Summary-Due-Soon-Count", "Due soon"],
      ["Transcript-Summary-No-Due-Date-Count", "Assigned/No due date"],
    ].map(([type, text]) => {
      const span = container.querySelector(`span[data-tag="${type}"]`);
      return {
        value: span.textContent,
        text,
      };
    });
    return {
      view: viewLink.getAttribute("href"),
      data: transcripts,
    };
  }

  getPastDue() {
    const items = [
      ...this.dom.querySelectorAll(
        'div[data-tag="PastDue-Container"] div[data-tag="PastDue-Item"]'
      ),
    ];

    if (!items) {
      return [];
    }

    return items.map((o) => {
      const title = o.querySelector(
        'div[data-tag="PastDue-Title"] .p-MultiLineFade'
      ).textContent;
      const status = this.getTextContentByDataTag(o, "PastDue-Status");
      const daysDue = this.getTextContentByDataTag(o, "PastDue-DaysDue");
      return {
        title,
        status,
        daysDue,
      };
    });
  }

  getAssigned() {
    return [];
  }
}

export const sleep = (ms = 1000) =>
  new Promise((resolve) => setTimeout(resolve, ms));



// WEBPACK FOOTER //
// ./src/frontend/components/LearnerHome/util.js